import { docEntityConstraints } from "building-types/doc/docEntityConstraints";
import type { DocEntityForeignKeyConstraint } from "building-types/doc/docEntityConstraintTypes";
import type { DocEntityDeletionConstraint } from "building-types/doc/docEntityScopeConstraints";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import {
  docEntityTypeToChildTypes,
  docEntityTypeToParentTypes,
  docEntityTypeToReverseReferenceTypes,
} from "building-types/doc/docEntityTypeToForeignKeyRelations";
import { docRefReverseTraversalTargetEntities } from "building-types/entities/docRefReverseTraversalTargetEntities";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { assert } from "common/utils/assertOfType";
import { docAssociationTableMemberTypesForContainer } from "../docAssociationTableTools";
import { getDocTableNamesForEntityTypes } from "../docEntityTableTools";
import type { DocEntityNode } from "./docEntityNode";
import type { docEntityTypeToDocTableName } from "./docEntityTypeToDocTableName";
import { docEntityReferenceTargetsEntity } from "./docSchemaForeignKeyUtils";

function getTopLevelForeignKeyConstraintFieldKey(fieldPath: string): string {
  const segments = fieldPath.split(".").filter((segment) => segment.length > 0);
  return segments[0] ?? fieldPath;
}

/**
 * Returns the configured onDelete behavior for a source entity FK field targeting targetType.
 * Defaults to DELETE when no explicit constraint exists.
 */
export function getForeignKeyDeleteConstraint(
  sourceType: DocEntityType,
  fieldPath: string,
  targetType: DocEntityType,
): DocEntityDeletionConstraint {
  const fieldKey = getTopLevelForeignKeyConstraintFieldKey(fieldPath);
  const entityConstraints = docEntityConstraints[sourceType];
  if (!entityConstraints) {
    return "DELETE";
  }

  const fieldConstraints = entityConstraints[fieldKey as keyof typeof entityConstraints];
  if (!fieldConstraints || typeof fieldConstraints !== "object") {
    return "DELETE";
  }

  const targetConstraint = (fieldConstraints as Record<string, DocEntityForeignKeyConstraint | undefined>)[targetType];
  return targetConstraint?.onDelete ?? "DELETE";
}

/**
 * Returns true when a reverse-referencing entity should be deleted because a referenced
 * doc-ref entity was removed under CASCADING_DELETE resolution.
 *
 * Returns false when:
 * 1. Every FK path from referrer -> target has `onDelete: "IGNORE"`. The referrer is kept.
 *    Scalar / table FKs are cleared via `docDeleteEntityReverseReferences`; formula expression
 *    mentions are left in place (they are not scalar FK slots).
 * 2. No FK path from referrer -> target was found in `getForeignKeyReferences()`. The referrer
 *    is kept; stale FK fields are cleared separately via `docDeleteEntityReverseReferences`.
 */
export function shouldCascadeDeleteReverseReference(
  referrerNode: DocEntityNode,
  targetHandle: DocEntityHandle,
  targetEntityId: string | undefined,
): boolean {
  for (const reference of referrerNode.getForeignKeyReferences()) {
    if (!docEntityReferenceTargetsEntity(reference, targetHandle.type, targetEntityId)) {
      continue;
    }

    const deleteConstraint = getForeignKeyDeleteConstraint(
      referrerNode.getEntityType(),
      reference.fieldPath,
      targetHandle.type,
    );
    if (deleteConstraint !== "IGNORE") {
      return true;
    }
  }

  return false;
}

/**
 * Entity types that may reverse-reference `entityType` at runtime.
 *
 * Combines the generated schema FK reverse map with association-table member types
 * (e.g. OPTION_SET to COMPONENT via `componentAssociations`). Use this for reverse
 * discovery / scanning. Use getDocEntityReverseReferenceTypes for schema-only FK
 * capability.
 */
export function getDocEntityReverseReferenceCandidateTypes(entityType: DocEntityType): DocEntityType[] {
  return Array.from(
    new Set<DocEntityType>([
      ...docEntityTypeToReverseReferenceTypes[entityType],
      ...docAssociationTableMemberTypesForContainer(entityType),
    ]),
  );
}

/**
 * Get the parent types for a given entity type.
 * The spread operator is used to remove the readonly modifier to enable standard array operations.
 */
export function getDocEntityParentTypes<T extends DocEntityType>(
  entityType: T,
): (typeof docEntityTypeToParentTypes)[T][number][] {
  return [...docEntityTypeToParentTypes[entityType]];
}

/**
 * Get the table names for the parent entities of a given entity type.
 */
export function getDocEntityParentTableNames<T extends DocEntityType>(
  entityType: T,
): Exclude<(typeof docEntityTypeToDocTableName)[(typeof docEntityTypeToParentTypes)[T][number]], undefined>[] {
  return getDocTableNamesForEntityTypes(getDocEntityParentTypes(entityType));
}

/**
 * For group nodes, we need to get the parent types of their respective child type.
 */
export function docEntityNodeGetParentTypes(node: DocEntityNode): DocEntityType[] {
  if (node.getEntityType() === "GROUP") {
    const childTypes = node.getChildren().map((child) => child.getEntityType());
    const uniqueChildTypes = Array.from(new Set(childTypes));

    assert(uniqueChildTypes.length === 1, "Group must have only one child type");

    const [childType] = uniqueChildTypes;
    return getDocEntityParentTypes(childType);
  }

  return getDocEntityParentTypes(node.getEntityType());
}

/**
 * Check if a parent type is eligible for a given child entity type.
 */
export function isEligibleParentForEntityType(parentType: DocEntityType, childType: DocEntityType): boolean {
  const parentTypes: DocEntityType[] = getDocEntityParentTypes(childType);
  return parentTypes.includes(parentType);
}

/**
 * Check if an entity type requires a parent.
 */
export function docEntityTypeRequiresParent(type: DocEntityType): boolean {
  return getDocEntityParentTypes(type).length > 0;
}

/**
 * Get the child types for a given entity type.
 * The spread operator is used to remove the readonly modifier to enable standard array operations.
 */
export function getDocEntityChildTypes<T extends DocEntityType>(
  entityType: T,
): (typeof docEntityTypeToChildTypes)[T][number][] {
  return [...docEntityTypeToChildTypes[entityType]];
}

/**
 * Get the table names for the child entities of a given entity type.
 */
export function getDocEntityChildTableNames<T extends DocEntityType>(
  entityType: T,
): Exclude<(typeof docEntityTypeToDocTableName)[(typeof docEntityTypeToChildTypes)[T][number]], undefined>[] {
  return getDocTableNamesForEntityTypes(getDocEntityChildTypes(entityType));
}

/**
 * Check if a child type is eligible for a given parent entity type.
 */
export function isEligibleChildForEntityType(childType: DocEntityType, parentType: DocEntityType): boolean {
  const childTypes: DocEntityType[] = getDocEntityChildTypes(parentType);
  return childTypes.includes(childType);
}

/**
 * Returns true if reverse traversal from targetType should include sourceType
 * based on explicit FK constraints on sourceType -> targetType.
 */
export function isReverseDocRefEligible(sourceType: DocEntityType, targetType: DocEntityType): boolean {
  const entityConstraints = docEntityConstraints[sourceType];

  if (!entityConstraints) {
    return false;
  }

  for (const fieldConstraints of Object.values(entityConstraints)) {
    if (!fieldConstraints) {
      continue;
    }

    const targetConstraint = fieldConstraints[targetType] as DocEntityForeignKeyConstraint | undefined;

    if (targetConstraint?.onDocRefReverseReferences === "REQUIRED") {
      return true;
    }
  }

  return false;
}

/**
 * Returns true if any FK constraint explicitly opts in reverse doc ref traversal to targetType.
 */
export function shouldTraverseReverseReferencesForTargetType(targetType: DocEntityType): boolean {
  return !!docRefReverseTraversalTargetEntities[targetType];
}
